// UC25 — the aggregate definition, written in the administrator's words.
// per wireframes/react/src/pages/AdminAgregari.tsx (editorul: Identificare /
//   Calcul / Actualizare și retenție)
// per docs/caiet-uc-spec.md §UC25-01 §UC-AGG-01 §UC-AGG-02 §UC-AGG-03 §UC-AGG-06
//   §UC-AGG-10
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudCheckbox,
  MudIcon,
  MudInlineMessage,
  MudTextInput,
  MudNumericInput,
  MudSegmentedControl,
  MudSelect,
  MudSwitch,
  MudTag,
  MudTextarea,
  MudTooltip,
} from '@egov-moldova/mud-react';
import { useToast } from '@simsm/ui-shared';
import { type ApiRefusal, apiClient, apiRefusal, loadFailureKind } from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import {
  AGGREGATE_NAME_RE,
  AGGREGATE_SOURCES,
  BUCKET_INTERVALS,
  INTERVAL_UNITS,
  MAX_CALCULATIONS,
  MAX_DIMENSIONS,
  RESULT_NAME_RE,
  RETENTION_PRESETS,
  RETENTION_UNITS,
  SCHEDULE_PRESETS,
  dimensionsFor,
  getAggregate,
  isExpression,
  isPresetRetention,
  isPresetSchedule,
  listAggregates,
  measuresFor,
  slugify,
  splitInterval,
  suggestResultName,
  type AggregateFunction,
  type AggregateSource,
  type AggregateView,
  type AggregateWriteBody,
  type Calculation,
  type ExpressionTemplate,
} from './aggregates-domain';
import './aggregates.css';

const CUSTOM = '__custom__';
const EXPRESSION_TEMPLATES: ExpressionTemplate[] = ['ratio', 'difference'];

interface FormState {
  displayName: string;
  description: string;
  source: AggregateSource;
  baseAggregate: string;
  bucketInterval: string;
  groupBy: string[];
  calculations: Calculation[];
  excludeExpiredLots: boolean;
  scheduleMode: 'preset' | 'custom';
  schedulePreset: string;
  scheduleAmount: string;
  scheduleUnit: string;
  retentionMode: 'preset' | 'custom';
  retentionPreset: string;
  retentionAmount: string;
  retentionUnit: string;
}

function emptyForm(): FormState {
  return {
    displayName: '',
    description: '',
    source: 'stock_movements',
    baseAggregate: '',
    bucketInterval: '1 day',
    groupBy: ['medicine_name'],
    calculations: [
      { kind: 'standard', fn: 'SUM', column: 'quantity_signed', alias: 'cantitate_totala' },
    ],
    excludeExpiredLots: true,
    scheduleMode: 'preset',
    schedulePreset: '1 day',
    scheduleAmount: '2',
    scheduleUnit: 'hours',
    retentionMode: 'preset',
    retentionPreset: 'permanent',
    retentionAmount: '18',
    retentionUnit: 'months',
  };
}

function toForm(aggregate: AggregateView): FormState {
  const base = emptyForm();
  const schedule = splitInterval(aggregate.refreshScheduleInterval);
  const retention = splitInterval(aggregate.retentionInterval ?? '');
  const scheduleIsPreset = isPresetSchedule(aggregate.refreshScheduleInterval);
  const retentionIsPreset = isPresetRetention(aggregate.retentionInterval);
  return {
    displayName: aggregate.displayName,
    description: aggregate.description ?? '',
    source: aggregate.source,
    baseAggregate: aggregate.baseAggregate ?? '',
    bucketInterval: aggregate.bucketInterval,
    groupBy: aggregate.groupBy,
    calculations: aggregate.calculations.map((calculation) => ({ ...calculation })),
    excludeExpiredLots: aggregate.excludeExpiredLots,
    scheduleMode: scheduleIsPreset ? 'preset' : 'custom',
    schedulePreset: scheduleIsPreset ? aggregate.refreshScheduleInterval : base.schedulePreset,
    scheduleAmount: !scheduleIsPreset && schedule ? schedule.amount : base.scheduleAmount,
    scheduleUnit: !scheduleIsPreset && schedule ? schedule.unit : base.scheduleUnit,
    retentionMode: retentionIsPreset ? 'preset' : 'custom',
    retentionPreset: retentionIsPreset ? (aggregate.retentionInterval ?? 'permanent') : 'permanent',
    retentionAmount: !retentionIsPreset && retention ? retention.amount : base.retentionAmount,
    retentionUnit: !retentionIsPreset && retention ? retention.unit : base.retentionUnit,
  };
}

function scheduleValue(form: FormState): string {
  return form.scheduleMode === 'custom'
    ? `${form.scheduleAmount.trim()} ${form.scheduleUnit}`
    : form.schedulePreset;
}

function retentionValue(form: FormState): string | null {
  if (form.retentionMode === 'custom')
    return `${form.retentionAmount.trim()} ${form.retentionUnit}`;
  return form.retentionPreset === 'permanent' ? null : form.retentionPreset;
}

// UC-AGG-06 lets an aggregate read another aggregate, so the picker has to refuse
// the cycle that would make either of them unbuildable.
function dependsOn(candidate: string, target: string, byName: Map<string, AggregateView>): boolean {
  const seen = new Set<string>();
  let current: string | null = candidate;
  while (current && !seen.has(current)) {
    if (current === target) return true;
    seen.add(current);
    current = byName.get(current)?.baseAggregate ?? null;
  }
  return false;
}

export function AggregateFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { name } = useParams<{ name: string }>();
  const isEdit = Boolean(name);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [initial, setInitial] = useState<FormState>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [refusal, setRefusal] = useState<ApiRefusal | null>(null);

  const detailQuery = useQuery({
    queryKey: ['admin-aggregate', name],
    queryFn: () => getAggregate(name ?? ''),
    enabled: isEdit,
    retry: false,
  });

  const listQuery = useQuery({ queryKey: ['admin-aggregates'], queryFn: listAggregates });
  const all = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const byName = useMemo(() => new Map(all.map((item) => [item.name, item])), [all]);

  useEffect(() => {
    if (detailQuery.data) {
      const next = toForm(detailQuery.data);
      setForm(next);
      setInitial(next);
    }
  }, [detailQuery.data]);

  const base = form.source === 'aggregate' ? (byName.get(form.baseAggregate) ?? null) : null;
  const availableDimensions = dimensionsFor(form.source, base);
  const measures = measuresFor(form.source, base);
  const standardAliases = form.calculations
    .filter((calculation) => !isExpression(calculation))
    .map((calculation) => calculation.alias.trim())
    .filter(Boolean);

  function measureLabel(column: string): string {
    return t(`aggregates.measure.${column}`, column);
  }

  const derivedName = isEdit ? (name ?? '') : slugify(form.displayName).slice(0, 80);
  const nameTaken =
    !isEdit && derivedName.length > 0 && all.some((item) => item.name === derivedName);

  const displayNameError = !form.displayName.trim()
    ? t('aggregates.form.error.display_name_required')
    : nameTaken
      ? t('aggregates.form.error.name_taken')
      : !isEdit && !AGGREGATE_NAME_RE.test(derivedName)
        ? t('aggregates.form.error.name_unusable')
        : null;
  const baseError =
    form.source === 'aggregate' && !form.baseAggregate
      ? t('aggregates.form.error.base_required')
      : null;
  const dimensionsError =
    form.groupBy.length === 0
      ? t('aggregates.form.error.dimensions_required')
      : form.groupBy.length > MAX_DIMENSIONS
        ? t('aggregates.form.error.dimensions_max')
        : null;

  function calculationError(calculation: Calculation, index: number): string | null {
    const alias = calculation.alias.trim();
    if (!alias) return t('aggregates.form.error.result_name_required');
    if (!RESULT_NAME_RE.test(alias)) return t('aggregates.form.error.result_name_shape');
    if (form.calculations.filter((other) => other.alias.trim() === alias).length > 1) {
      return t('aggregates.form.error.result_name_unique');
    }
    if (isExpression(calculation)) {
      const before = form.calculations
        .slice(0, index)
        .filter((other) => !isExpression(other))
        .map((other) => other.alias.trim());
      if (!calculation.firstOperand || !calculation.secondOperand) {
        return t('aggregates.form.error.operands_required');
      }
      if (calculation.firstOperand === calculation.secondOperand) {
        return t('aggregates.form.error.operands_distinct');
      }
      if (
        !before.includes(calculation.firstOperand) ||
        !before.includes(calculation.secondOperand)
      ) {
        return t('aggregates.form.error.operands_earlier');
      }
      return null;
    }
    const measure = measures.find((item) => item.column === calculation.column);
    if (!measure) return t('aggregates.form.error.measure_required');
    if (!measure.functions.includes(calculation.fn)) {
      return t('aggregates.form.error.function_unavailable');
    }
    return null;
  }

  const calculationsError =
    form.calculations.length === 0
      ? t('aggregates.form.error.calculations_required')
      : form.calculations.length > MAX_CALCULATIONS
        ? t('aggregates.form.error.calculations_max')
        : (form.calculations.map(calculationError).find(Boolean) ?? null);
  const scheduleError =
    form.scheduleMode === 'custom' && !/^[1-9]\d*$/.test(form.scheduleAmount.trim())
      ? t('aggregates.form.error.positive_integer')
      : null;
  const retentionError =
    form.retentionMode === 'custom' && !/^[1-9]\d*$/.test(form.retentionAmount.trim())
      ? t('aggregates.form.error.positive_integer')
      : null;

  const valid =
    !displayNameError &&
    !baseError &&
    !dimensionsError &&
    !calculationsError &&
    !scheduleError &&
    !retentionError;

  const save = useMutation({
    mutationFn: (body: AggregateWriteBody) =>
      isEdit
        ? apiClient.patch<AggregateView>(`/admin/aggregates/${name}`, body).then((r) => r.data)
        : apiClient.post<AggregateView>('/admin/aggregates', body).then((r) => r.data),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-aggregates'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-aggregate', saved.name] });
      // The fișă this lands on looks the same whether the definition was just
      // written or merely opened, so the write has to say so itself.
      toast({
        variant: 'success',
        title: isEdit ? t('aggregates.toast.saved') : t('aggregates.toast.created'),
        text: saved.displayName || saved.name,
      });
      navigate(`/system-admin/aggregates/${saved.name}`);
    },
    // Per-operation fallback: `form.error.save_failed` is right for the save and
    // is the only write this screen makes.
    onError: (err: unknown) => setRefusal(apiRefusal(err, 'form.error.save_failed')),
  });

  function submit(): void {
    setSubmitted(true);
    if (!valid) return;
    setRefusal(null);
    const body: AggregateWriteBody = {
      displayName: form.displayName.trim(),
      description: form.description.trim(),
      source: form.source,
      bucketInterval: form.bucketInterval,
      groupBy: form.groupBy,
      calculations: form.calculations.map((calculation) => ({
        ...calculation,
        alias: calculation.alias.trim(),
      })),
      excludeExpiredLots: form.excludeExpiredLots,
      retentionInterval: retentionValue(form),
      refreshScheduleInterval: scheduleValue(form),
    };
    if (form.source === 'aggregate') body.baseAggregate = form.baseAggregate;
    if (!isEdit) body.name = derivedName;
    save.mutate(body);
  }

  function patchCalculation(index: number, patch: Partial<Calculation>): void {
    setForm((current) => ({
      ...current,
      calculations: current.calculations.map((calculation, i) =>
        i === index ? ({ ...calculation, ...patch } as Calculation) : calculation,
      ),
    }));
  }

  function addStandard(): void {
    setForm((current) => {
      if (current.calculations.length >= MAX_CALCULATIONS) return current;
      const measure = measures[0];
      if (!measure) return current;
      const fn = measure.functions[0] as AggregateFunction;
      return {
        ...current,
        calculations: [
          ...current.calculations,
          {
            kind: 'standard',
            fn,
            column: measure.column,
            alias: `valoare_${current.calculations.length + 1}`,
          },
        ],
      };
    });
  }

  function addExpression(): void {
    setForm((current) => {
      if (current.calculations.length >= MAX_CALCULATIONS) return current;
      const aliases = current.calculations
        .filter((calculation) => !isExpression(calculation))
        .map((calculation) => calculation.alias.trim())
        .filter(Boolean);
      if (aliases.length < 2) return current;
      return {
        ...current,
        calculations: [
          ...current.calculations,
          {
            kind: 'expression',
            template: 'ratio',
            firstOperand: aliases[0] as string,
            secondOperand: aliases[1] as string,
            alias: `rezultat_${current.calculations.length + 1}`,
          },
        ],
      };
    });
  }

  function removeCalculation(index: number): void {
    setForm((current) => {
      if (current.calculations.length <= 1) return current;
      const removed = current.calculations[index];
      const removedAlias = removed ? removed.alias.trim() : '';
      return {
        ...current,
        calculations: current.calculations
          .filter((_, i) => i !== index)
          .map((calculation) =>
            isExpression(calculation)
              ? {
                  ...calculation,
                  firstOperand:
                    calculation.firstOperand === removedAlias ? '' : calculation.firstOperand,
                  secondOperand:
                    calculation.secondOperand === removedAlias ? '' : calculation.secondOperand,
                }
              : calculation,
          ),
      };
    });
  }

  function toggleDimension(dimension: string, checked: boolean): void {
    setForm((current) => ({
      ...current,
      groupBy: checked
        ? current.groupBy.includes(dimension)
          ? current.groupBy
          : [...current.groupBy, dimension]
        : current.groupBy.filter((value) => value !== dimension),
    }));
  }

  const loadError = !isEdit || !detailQuery.isError ? null : loadFailureKind(detailQuery.error);

  return (
    <FormPage
      breadcrumb={[
        { label: t('aggregates.title'), href: '/system-admin/aggregates' },
        {
          label: isEdit ? t('aggregates.form.edit_title') : t('aggregates.form.create_title'),
        },
      ]}
      title={isEdit ? t('aggregates.form.edit_title') : t('aggregates.form.create_title')}
      lede={t('aggregates.form.lede')}
      saveLabel={isEdit ? t('aggregates.form.save_action') : t('aggregates.form.create_action')}
      isSaving={save.isPending}
      isDirty={JSON.stringify(form) !== JSON.stringify(initial)}
      isLoading={detailQuery.isLoading}
      loadError={loadError}
      refusal={refusal}
      onSave={submit}
      onCancel={() =>
        navigate(isEdit ? `/system-admin/aggregates/${name}` : '/system-admin/aggregates')
      }
    >
      <FormSection
        title={t('aggregates.section.identity')}
        lede={t('aggregates.section.identity_lede')}
      >
        <FormField
          label={t('aggregates.form.display_name')}
          required
          fullWidth
          hint={t('aggregates.form.display_name_hint')}
          error={submitted ? (displayNameError ?? undefined) : undefined}
        >
          <MudTextInput
            size="md"
            value={form.displayName}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, displayName: e.detail.value })
            }
          />
        </FormField>
        <FormField label={t('aggregates.form.description')} fullWidth>
          <MudTextarea
            size="md"
            rows={3}
            value={form.description}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, description: e.detail.value })
            }
          />
        </FormField>
      </FormSection>

      <FormSection
        title={t('aggregates.section.calculation')}
        lede={t('aggregates.section.calculation_lede')}
      >
        <FormRow>
          <FormField label={t('aggregates.form.source')} required>
            <MudSelect
              size="medium"
              value={form.source}
              onMudChange={(e: CustomEvent<{ value: string }>) => {
                const source = e.detail.value as AggregateSource;
                setForm({
                  ...form,
                  source,
                  baseAggregate: '',
                  groupBy: source === 'stock_movements' ? ['medicine_name'] : [],
                  calculations:
                    source === 'stock_movements'
                      ? [
                          {
                            kind: 'standard',
                            fn: 'SUM',
                            column: 'quantity_signed',
                            alias: 'cantitate_totala',
                          },
                        ]
                      : [],
                });
              }}
            >
              {AGGREGATE_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {t(`aggregates.source.${source}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
          {form.source === 'aggregate' ? (
            <FormField
              label={t('aggregates.form.base')}
              required
              hint={t('aggregates.form.base_hint')}
              error={submitted ? (baseError ?? undefined) : undefined}
            >
              <MudSelect
                size="medium"
                value={form.baseAggregate}
                placeholder={t('aggregates.form.base_placeholder')}
                onMudChange={(e: CustomEvent<{ value: string }>) => {
                  const nextBase = byName.get(e.detail.value) ?? null;
                  setForm({
                    ...form,
                    baseAggregate: e.detail.value,
                    groupBy: nextBase ? nextBase.groupBy.slice(0, 1) : [],
                    calculations: nextBase
                      ? [
                          {
                            kind: 'standard',
                            fn: 'SUM',
                            column: nextBase.calculations[0]?.alias ?? '',
                            alias: nextBase.calculations[0]?.alias ?? 'valoare_totala',
                          },
                        ]
                      : [],
                  });
                }}
              >
                {all
                  .filter((item) => item.name !== name)
                  .map((item) => (
                    <option
                      key={item.name}
                      value={item.name}
                      disabled={isEdit && dependsOn(item.name, name ?? '', byName)}
                    >
                      {item.displayName}
                    </option>
                  ))}
              </MudSelect>
            </FormField>
          ) : null}
        </FormRow>

        <fieldset className="simsm-aggregate__fieldset">
          <legend>
            {t('aggregates.form.dimensions')}
            <span aria-hidden="true"> *</span>
          </legend>
          <div className="simsm-aggregate__dimension-grid">
            {availableDimensions.map((dimension) => (
              <MudCheckbox
                key={dimension}
                size="md"
                checked={form.groupBy.includes(dimension)}
                disabled={
                  !form.groupBy.includes(dimension) && form.groupBy.length >= MAX_DIMENSIONS
                }
                onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                  toggleDimension(dimension, e.detail.checked)
                }
              >
                <span slot="label">{t(`aggregates.dimension.${dimension}`, dimension)}</span>
              </MudCheckbox>
            ))}
          </div>
          {/* The wireframe's `assistive` line under a field (AdminAgregari.tsx
              :875-879): plain text, no icon, red only while it carries the
              error. AGE's inline message is that line as a component. */}
          {availableDimensions.length === 0 ? (
            <MudInlineMessage size="small" variant="info" hideIcon>
              {t('aggregates.form.dimensions_pick_base')}
            </MudInlineMessage>
          ) : null}
          {submitted && dimensionsError ? (
            <MudInlineMessage size="small" variant="error" hideIcon role="alert">
              {dimensionsError}
            </MudInlineMessage>
          ) : (
            <MudInlineMessage size="small" variant="info" hideIcon>
              {t('aggregates.form.dimensions_hint', {
                count: form.groupBy.length,
                max: MAX_DIMENSIONS,
              })}
            </MudInlineMessage>
          )}
        </fieldset>

        <fieldset className="simsm-aggregate__fieldset">
          <legend>
            {t('aggregates.form.calculations')}
            <span aria-hidden="true"> *</span>
            <MudTooltip content={t('aggregates.form.calculations_tip')} position="top">
              {/* Unslotted, this icon lands in the bubble instead of on the
                  trigger, and the tip renders 0x0 — see SectionTip in
                  MedicineFormPage.tsx. */}
              <MudIcon slot="trigger" name="circle-info" size={16} />
            </MudTooltip>
          </legend>
          <div className="simsm-aggregate__results">
            {form.calculations.map((calculation, index) => {
              const error = submitted ? calculationError(calculation, index) : null;
              const earlierAliases = form.calculations
                .slice(0, index)
                .filter((other) => !isExpression(other))
                .map((other) => other.alias.trim())
                .filter(Boolean);
              const measure = isExpression(calculation)
                ? undefined
                : measures.find((item) => item.column === calculation.column);
              return (
                <div
                  className="simsm-aggregate__result simsm-aggregate__result--editable"
                  data-invalid={error ? 'true' : undefined}
                  key={index}
                >
                  <div className="simsm-aggregate__result-head">
                    <strong>{t('aggregates.form.result_n', { index: index + 1 })}</strong>
                    <MudTag
                      variant="status"
                      type="subtle"
                      semantic="neutral"
                      label={
                        isExpression(calculation)
                          ? t('aggregates.form.kind.expression')
                          : t('aggregates.form.kind.standard')
                      }
                    />
                    <MudButton
                      shape="circular"
                      variant="neutral"
                      size="sm"
                      disabled={form.calculations.length <= 1}
                      onClick={() => removeCalculation(index)}
                    >
                      {t('aggregates.form.remove_result')}
                    </MudButton>
                  </div>

                  {isExpression(calculation) ? (
                    <FormRow>
                      <FormField label={t('aggregates.form.expression_template')} required>
                        <MudSelect
                          size="medium"
                          value={calculation.template}
                          onMudChange={(e: CustomEvent<{ value: string }>) =>
                            patchCalculation(index, {
                              template: e.detail.value as ExpressionTemplate,
                            })
                          }
                        >
                          {EXPRESSION_TEMPLATES.map((template) => (
                            <option key={template} value={template}>
                              {t(`aggregates.expression.${template}`)}
                            </option>
                          ))}
                        </MudSelect>
                      </FormField>
                      <FormField label={t('aggregates.form.first_operand')} required>
                        <MudSelect
                          size="medium"
                          value={calculation.firstOperand}
                          placeholder={t('aggregates.form.operand_placeholder')}
                          onMudChange={(e: CustomEvent<{ value: string }>) =>
                            patchCalculation(index, { firstOperand: e.detail.value })
                          }
                        >
                          {earlierAliases.map((alias) => (
                            <option key={alias} value={alias}>
                              {alias}
                            </option>
                          ))}
                        </MudSelect>
                      </FormField>
                      <FormField label={t('aggregates.form.second_operand')} required>
                        <MudSelect
                          size="medium"
                          value={calculation.secondOperand}
                          placeholder={t('aggregates.form.operand_placeholder')}
                          onMudChange={(e: CustomEvent<{ value: string }>) =>
                            patchCalculation(index, { secondOperand: e.detail.value })
                          }
                        >
                          {earlierAliases.map((alias) => (
                            <option key={alias} value={alias}>
                              {alias}
                            </option>
                          ))}
                        </MudSelect>
                      </FormField>
                    </FormRow>
                  ) : (
                    <FormRow>
                      <FormField label={t('aggregates.form.function')} required>
                        <MudSelect
                          size="medium"
                          value={calculation.fn}
                          onMudChange={(e: CustomEvent<{ value: string }>) => {
                            const fn = e.detail.value as AggregateFunction;
                            patchCalculation(index, {
                              fn,
                              alias: suggestResultName(fn, measureLabel(calculation.column)),
                            });
                          }}
                        >
                          {(measure?.functions ?? []).map((fn) => (
                            <option key={fn} value={fn}>
                              {t(`aggregates.function.${fn}`)}
                            </option>
                          ))}
                        </MudSelect>
                      </FormField>
                      <FormField label={t('aggregates.form.measure')} required>
                        <MudSelect
                          size="medium"
                          value={calculation.column}
                          onMudChange={(e: CustomEvent<{ value: string }>) => {
                            const next = measures.find((item) => item.column === e.detail.value);
                            const fn = (
                              next?.functions.includes(calculation.fn)
                                ? calculation.fn
                                : (next?.functions[0] ?? 'SUM')
                            ) as AggregateFunction;
                            patchCalculation(index, {
                              column: e.detail.value,
                              fn,
                              alias: suggestResultName(fn, measureLabel(e.detail.value)),
                            });
                          }}
                        >
                          {measures.map((item) => (
                            <option key={item.column} value={item.column}>
                              {measureLabel(item.column)}
                            </option>
                          ))}
                        </MudSelect>
                      </FormField>
                    </FormRow>
                  )}

                  <FormField
                    label={t('aggregates.form.result_name')}
                    required
                    fullWidth
                    hint={t('aggregates.form.result_name_hint')}
                    error={error ?? undefined}
                  >
                    <MudTextInput
                      size="md"
                      value={calculation.alias}
                      onMudInput={(e: CustomEvent<{ value: string }>) =>
                        patchCalculation(index, { alias: e.detail.value })
                      }
                    />
                  </FormField>
                </div>
              );
            })}
          </div>
          <div className="simsm-aggregate__result-actions">
            <MudButton
              shape="circular"
              variant="neutral"
              size="sm"
              disabled={form.calculations.length >= MAX_CALCULATIONS || measures.length === 0}
              onClick={addStandard}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('aggregates.form.add_function')}
            </MudButton>
            <MudButton
              shape="circular"
              variant="neutral"
              size="sm"
              disabled={form.calculations.length >= MAX_CALCULATIONS || standardAliases.length < 2}
              onClick={addExpression}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('aggregates.form.add_expression')}
            </MudButton>
            <MudInlineMessage size="small" variant="info" hideIcon>
              {t('aggregates.form.calculations_count', {
                count: form.calculations.length,
                max: MAX_CALCULATIONS,
              })}
            </MudInlineMessage>
          </div>
          {standardAliases.length < 2 ? (
            <MudInlineMessage size="small" variant="info" hideIcon>
              {t('aggregates.form.expression_needs_two')}
            </MudInlineMessage>
          ) : null}
        </fieldset>

        <FormField label={t('aggregates.form.bucket')} required fullWidth>
          <MudSegmentedControl
            size="md"
            aria-label={t('aggregates.form.bucket')}
            value={form.bucketInterval}
            segments={BUCKET_INTERVALS.map((bucket) => ({
              value: bucket,
              label: t(`aggregates.bucket.${bucket}`),
            }))}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, bucketInterval: e.detail.value })
            }
          />
        </FormField>

        {form.source === 'stock_movements' ? (
          <div className="simsm-aggregate__switch-row">
            <MudSwitch
              checked={form.excludeExpiredLots}
              label={t('aggregates.form.exclude_expired')}
              onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                setForm({ ...form, excludeExpiredLots: e.detail.checked })
              }
            />
            <MudTooltip content={t('aggregates.form.exclude_expired_hint')} position="top">
              <MudIcon slot="trigger" name="circle-info" size={16} />
            </MudTooltip>
          </div>
        ) : null}
      </FormSection>

      <FormSection
        title={t('aggregates.section.schedule')}
        lede={t('aggregates.section.schedule_lede')}
      >
        <FormRow>
          <FormField label={t('aggregates.form.schedule')} required>
            <MudSelect
              size="medium"
              value={form.scheduleMode === 'custom' ? CUSTOM : form.schedulePreset}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm(
                  e.detail.value === CUSTOM
                    ? { ...form, scheduleMode: 'custom' }
                    : { ...form, scheduleMode: 'preset', schedulePreset: e.detail.value },
                )
              }
            >
              {SCHEDULE_PRESETS.map((preset) => (
                <option key={preset} value={preset}>
                  {t(`aggregates.schedule.${preset}`)}
                </option>
              ))}
              <option value={CUSTOM}>{t('aggregates.schedule.custom')}</option>
            </MudSelect>
          </FormField>
          <FormField label={t('aggregates.form.retention')} required>
            <MudSelect
              size="medium"
              value={form.retentionMode === 'custom' ? CUSTOM : form.retentionPreset}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm(
                  e.detail.value === CUSTOM
                    ? { ...form, retentionMode: 'custom' }
                    : { ...form, retentionMode: 'preset', retentionPreset: e.detail.value },
                )
              }
            >
              {RETENTION_PRESETS.map((preset) => (
                <option key={preset} value={preset}>
                  {t(`aggregates.retention.${preset}`)}
                </option>
              ))}
              <option value={CUSTOM}>{t('aggregates.retention.custom')}</option>
            </MudSelect>
          </FormField>
        </FormRow>

        {form.scheduleMode === 'custom' ? (
          <FormRow>
            <FormField
              label={t('aggregates.form.schedule_every')}
              required
              error={submitted ? (scheduleError ?? undefined) : undefined}
            >
              <MudNumericInput
                size="md"
                min={1}
                step={1}
                value={Number(form.scheduleAmount) || 1}
                onMudInput={(e: CustomEvent<{ value: number | null }>) =>
                  setForm({ ...form, scheduleAmount: String(e.detail.value ?? '') })
                }
              />
            </FormField>
            <FormField label={t('aggregates.form.unit')} required>
              <MudSelect
                size="medium"
                value={form.scheduleUnit}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, scheduleUnit: e.detail.value })
                }
              >
                {INTERVAL_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {t(`aggregates.unit.${unit}`)}
                  </option>
                ))}
              </MudSelect>
            </FormField>
          </FormRow>
        ) : null}

        {form.retentionMode === 'custom' ? (
          <FormRow>
            <FormField
              label={t('aggregates.form.retention_duration')}
              required
              error={submitted ? (retentionError ?? undefined) : undefined}
            >
              <MudNumericInput
                size="md"
                min={1}
                step={1}
                value={Number(form.retentionAmount) || 1}
                onMudInput={(e: CustomEvent<{ value: number | null }>) =>
                  setForm({ ...form, retentionAmount: String(e.detail.value ?? '') })
                }
              />
            </FormField>
            <FormField label={t('aggregates.form.unit')} required>
              <MudSelect
                size="medium"
                value={form.retentionUnit}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, retentionUnit: e.detail.value })
                }
              >
                {RETENTION_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {t(`aggregates.unit.${unit}`)}
                  </option>
                ))}
              </MudSelect>
            </FormField>
          </FormRow>
        ) : null}
      </FormSection>
    </FormPage>
  );
}
